import { isEmpty } from 'lodash';

import type {
  ProposalImage,
  ProposalListInfo,
  ProposalListInfoAPIResponse,
  ProposalListMember,
  ProposalListMemberAPI,
  ProposalPageSortOption,
  StatisticsTierLabels
} from 'types';
import type {
  ProposalTileChannelType,
  ProposalTileChannelTypeOptions
} from 'modules/proposal/types/ProposalTile.type';

export const POSTS_AMOUNT_INSTAGRAM = 8;
export const POSTS_AMOUNT_INSTAGRAM_LEGACY = 5;
export const POSTS_AMOUNT_TIKTOK = 4;
export const DEFAULT_LIST_CHANNELS: Array<ProposalTileChannelType> = ['instagram'];
export const DEFAULT_MEMBER_CUSTOM_CHANNELS: Array<ProposalTileChannelType> = ['instagram', 'tiktok'];


export const PROPOSAL_SORT_TO_QUERY_PARAM: Record<ProposalPageSortOption, string> = {
  ig_asc: 'ig_followers.asc',
  ig_desc: 'ig_followers.desc',
  tt_asc: 'tt_followers.asc',
  tt_desc: 'tt_followers.desc'
};

function nonEmptyPosts(posts: Array<ProposalImage>): Array<ProposalImage> {
  return posts.filter(post => post && 'url' in post && post.url);
}

export function isTileIncomplete(
  listChannels: Array<ProposalTileChannelTypeOptions>,
  entry: ProposalListMember
): boolean {
  const selectedChannels = (listChannels.includes('custom'))
    ? entry.selectedChannels ?? DEFAULT_MEMBER_CUSTOM_CHANNELS
    : listChannels;
  const usedChannels = selectedChannels
    .filter((channel) => !isEmpty(entry.creatorProfile.channels[channel]));

  if (usedChannels.length === 0) {
    // always incomplete if has no channels
    return true;
  }

  return usedChannels.reduce<boolean>((result, channel) => {
    if (channel === 'tiktok') {
      return result || nonEmptyPosts(entry.images.tiktok || []).length < POSTS_AMOUNT_TIKTOK;
    }

    if (channel === 'instagram') {
      return result || nonEmptyPosts(entry.images.instagram || []).length < POSTS_AMOUNT_INSTAGRAM;
    }

    return result;
  }, false);
}

export function mapListAPIResponseEntry(entry: ProposalListMemberAPI): ProposalListMember {
  const channels = entry.selectedChannels ?? {};
  let selectedChannels = Object.keys(channels)
    .reduce<Array<ProposalTileChannelType>>((result, key) => {
      if (channels[key]) {
        result.push(key as ProposalTileChannelType);
      }

      return result;
    }, []);

  if (selectedChannels.length === 0) {
    selectedChannels = DEFAULT_MEMBER_CUSTOM_CHANNELS;
  }

  return {
    ...entry,
    images: {
      instagram: entry.instagramImages ?? [],
      tiktok: entry.tiktokImages ?? []
    },
    selectedChannels
  };
}

export function mapInternalListEntry(entry: ProposalListMember): ProposalListMemberAPI {
  const { selectedChannels, ...rest } = entry;

  const channelSelection: {
    [key in ProposalTileChannelType]: boolean
  } = { tiktok: false, instagram: false };

  selectedChannels.forEach(channel => {
    if (channel in channelSelection) {
      channelSelection[channel] = true;
    }
  });

  return {
    ...rest,
    instagramImages: entry.images.instagram,
    tiktokImages: entry.images.tiktok,
    images: undefined,
    selectedChannels: channelSelection
  };
}

const SUPPORTED_CHANNELS: Array<ProposalTileChannelType> = ['instagram', 'tiktok'];

export function mapListInfoAPIResponseEntry(entry: ProposalListInfoAPIResponse): ProposalListInfo {
  let selectedChannels = Object.keys(entry.channelSelection)
    .reduce<Array<ProposalTileChannelTypeOptions>>((result, key) => {
      if (entry.channelSelection[key]) {
        result.push(key as ProposalTileChannelTypeOptions);
      }

      return result;
    }, []);

  if (selectedChannels.length === 0) {
    selectedChannels = entry.availableChannels
      .filter(channel => SUPPORTED_CHANNELS.includes(channel));
  }

  return {
    ...entry,
    channels: selectedChannels
  };
}

export function mapInternalListInfoEntry(entry: ProposalListInfo): ProposalListInfoAPIResponse {
  const { channels, ...rest } = entry;

  const channelSelection: {
    [key in ProposalTileChannelTypeOptions]: boolean
  } = { custom: false, tiktok: false, instagram: false };

  channels.forEach(channel => {
    if (channel in channelSelection) {
      channelSelection[channel] = true;
    }
  });

  return {
    ...rest,
    channelSelection
  };
}

const SORT_ORDER_IG: Array<ProposalPageSortOption> = [
  'ig_asc',
  'ig_desc'
];

const SORT_ORDER_TT: Array<ProposalPageSortOption> = [
  'tt_asc',
  'tt_desc'
];

export function getAvailableSortOptions(currentChannels: Array<ProposalTileChannelTypeOptions>): Array<ProposalPageSortOption> {
  let sortList: Array<ProposalPageSortOption> = [];

  if (currentChannels.includes('instagram')) {
    sortList = sortList.concat(SORT_ORDER_IG);
  }

  if (currentChannels.includes('tiktok')) {
    sortList = sortList.concat(SORT_ORDER_TT);
  }

  return sortList;
}

function getMemberTier(currentSort: ProposalPageSortOption, member: ProposalListMember): StatisticsTierLabels {
  const channelFollowers: number = (
    currentSort.startsWith('ig')
      ? member.creatorProfile.channels.instagram?.metrics?.followers
      : member.creatorProfile.channels.tiktok?.metrics?.followers
  ) ?? 0;

  if (channelFollowers > 1000000) {
    return 'stars';
  }

  if (channelFollowers > 500000) {
    return 'giga';
  }

  if (channelFollowers > 250000) {
    return 'mega';
  }

  if (channelFollowers > 100000) {
    return 'macro';
  }

  if (channelFollowers > 50000) {
    return 'medium';
  }

  if (channelFollowers > 20000) {
    return 'micro';
  }

  return 'nano';
}

export function getGroupedMembers(
  creatorList: Array<ProposalListMember>,
  currentSort: ProposalPageSortOption
): Array<{ tier: string, data: Array<ProposalListMember> }> {
  const result: Array<{ tier: string, data: Array<ProposalListMember> }> = [];

  creatorList.forEach(member => {
    const tier = getMemberTier(currentSort, member);

    if (result.length === 0) {
      result.push({
        tier,
        data: []
      });
    }

    const lastEntry = result[result.length - 1];

    if (lastEntry.tier === tier) {
      lastEntry.data.push(member);
    } else {
      result.push({
        tier,
        data: [member]
      });
    }
  });

  return result;
}

export function getNewSort(
  selectedChannels: Array<ProposalTileChannelTypeOptions>,
  currentSort: ProposalPageSortOption
): ProposalPageSortOption {
  const availableSortOptions = getAvailableSortOptions(selectedChannels);

  if (availableSortOptions.includes(currentSort)) {
    return currentSort;
  }

  if (selectedChannels.includes('tiktok')) {
    return 'tt_desc';
  }

  return 'ig_desc';
}
